import { useEffect, useState } from "react";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { isPhoneApp } from "@/lib/phoneApp";
import { apiGet } from "@/lib/api";
import { useIsDesktop } from "@/lib/media";
import Splash from "@/screens/Splash";
import Auth from "@/screens/Auth";
import Pending from "@/screens/Pending";
import ChangePassword from "@/screens/ChangePassword";
import Home from "@/screens/Home";
import Times from "@/screens/Times";
import Give from "@/screens/Give";
import Community from "@/screens/Community";
import More from "@/screens/More";
import Detail from "@/screens/Detail";
import AI from "@/screens/AI";
import Admin from "@/screens/Admin";
import Profile from "@/screens/Profile";
import TabBar from "@/components/TabBar";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";

function AppInner() {
  const { user, loading, isAuthenticated, isPending, isAdmin, mustChangePassword, logout } = useAuth();
  const desktopViewport = useIsDesktop();
  const isDesktop = desktopViewport && !isPhoneApp;
  const [booting, setBooting] = useState(true);
  const [tab, setTab] = useState("home");
  const [detail, setDetail] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!isPhoneApp || !user?.id || !isAuthenticated || isPending || mustChangePassword) return;
    let active = true;
    apiGet("/profile").then(({ profile }) => {
      if (active && (!profile.phone || !profile.address || !profile.hebrew_name)) setProfileOpen(true);
    }).catch(() => {});
    return () => { active = false; };
  }, [user?.id, isAuthenticated, isPending, mustChangePassword]);

  if (booting || loading) return <div className="App"><Splash /></div>;
  if (!isAuthenticated) return <div className="App"><Auth /></div>;
  if (mustChangePassword) return <div className="App"><ChangePassword /></div>;
  if (isPending) return <div className="App"><Pending /></div>;

  const doLogout = () => { setDetail(null); setAiOpen(false); setAdminOpen(false); setProfileOpen(false); setTab("home"); logout(); };

  const screen = (
    <>
      {tab === "home" && <Home member={user} onNav={(t) => setTab(t)} onOpenDetail={setDetail} onOpenAI={() => setAiOpen(true)} />}
      {tab === "times" && <Times onHome={() => setTab("home")} />}
      {tab === "give" && <Give onDone={() => setTab("home")} />}
      {tab === "community" && <Community onOpenDetail={setDetail} onHome={() => setTab("home")} />}
      {tab === "more" && (
        <More
          member={user}
          isAdmin={isAdmin}
          onOpenDetail={setDetail}
          onOpenAI={() => setAiOpen(true)}
          onOpenAdmin={() => setAdminOpen(true)}
          onOpenProfile={() => setProfileOpen(true)}
          onHome={() => setTab("home")}
          onLogout={doLogout}
        />
      )}
    </>
  );

  // Desktop layout: top nav + full-width content + footer
  if (isDesktop) {
    return (
      <div className="App" data-testid="app-shell">
        <NavBar
          tab={tab}
          user={user}
          isAdmin={isAdmin}
          onSelect={(t) => { setTab(t); setDetail(null); setAiOpen(false); setAdminOpen(false); setProfileOpen(false); }}
          onOpenAdmin={() => setAdminOpen(true)}
          onOpenAI={() => setAiOpen(true)}
          onLogout={doLogout}
        />
        <main style={{ minHeight: "calc(100vh - 90px)", background: "var(--ls-cream)", position: "relative" }}>
          {screen}
          {detail && <Detail detail={detail} onClose={() => setDetail(null)} onNav={setTab} />}
          {aiOpen && <AI member={user} onClose={() => setAiOpen(false)} />}
          {adminOpen && isAdmin && <Admin onClose={() => setAdminOpen(false)} />}
          {profileOpen && <Profile onClose={() => setProfileOpen(false)} />}
        </main>
        <Footer />
      </div>
    );
  }

  // Mobile layout: phone shell + bottom tab bar
  return (
    <div className="App">
      <div className="ls-shell" data-testid="app-shell">
        {screen}
        {detail && <Detail detail={detail} onClose={() => setDetail(null)} onNav={setTab} />}
        {aiOpen && <AI member={user} onClose={() => setAiOpen(false)} />}
        {adminOpen && isAdmin && <Admin onClose={() => setAdminOpen(false)} />}
        {profileOpen && <Profile onClose={() => setProfileOpen(false)} />}
        <TabBar tab={aiOpen ? "ai" : tab} onSelect={(t) => { if (t !== "ai") setTab(t); setDetail(null); setAiOpen(t === "ai"); setAdminOpen(false); setProfileOpen(false); }} />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
